// authored by Alex Cooksey
import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';

import Wrapper from './Wrapper';

import iconSet from '../../images/teambuildr-selection.json';
import SearchIcon from '../../images/magnifying-glass.svg';
import { ColorGray } from '../../GlobalStyles';

const BottomDiv = styled('div')`
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const CloseButton = styled('button')`
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  height: 12px;
  justify-content: center;
  position: absolute;
  right: 20px;
  top: 20px;
  width: 12px;
  z-index: 100;
`;

const HeaderIconWrapper = styled('div')`
  align-items: center;
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
`;

export const ListItem = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  line-height: 32px;
  margin: ${(props) => (props.centerHorizontally ? '0 auto 4px auto' : '0 0 4px 0')};
  padding: 8px;
  text-align: left;
  width: ${(props) => (props.listItemWidth ? props.listItemWidth : 'calc(100% + 12px)')};
  z-index: 1;
  p {
    margin-bottom: 0;
    margin-left: ${(props) => props.itemMarginLeft || '0px'};
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHoverStrong};
    border-radius: 8px;
    font-weight: 500;
  }
  @media screen and (max-width: 440px) {
    width: 100%;
  }
`;

const ModalHeaderDiv = styled('div')`
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  /* Light keeps the legacy #979797 rule the feed and journal have always drawn;
     dark uses the border token because that grey vanishes on the dark panel. */
  border-bottom: 1px solid ${({ theme }) => (theme.isDark ? theme.colors.borderLight : ColorGray)};
  color: ${({ theme }) => theme.colors.text};
  /* The global stylesheet paints h4 near-black; the heading has to follow the
     panel's text token or it vanishes in dark. */
  h4 { color: inherit; }
  display: flex;
  flex-direction: column;
  font-weight: 500;
  height: ${(props) => (props.hasIcon ? 'auto' : '75px')};
  justify-content: flex-end;
  margin: 20px 0 0 0;
  padding: 10px 30px ${(props) => (props.hasIcon ? '16px' : '10px')};
  width: 100%;
  z-index: 1;
`;

export const NavigationWrapper = styled('div')`
  align-items: center;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: none;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  font-size: 12px;
  font-weight: 500;
  height: 25px;
  justify-content: end;
  margin-top: 10px;
  padding: 8px;
  top: 0;
  width: 100%;
  z-index: 1;
`;

const NoDataDiv = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 50px;
`;

const NoDataText = styled('p')`
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  margin-top: 10px;
`;

export const PrimaryWrapper = styled('div')`
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  font-family: 'Nunito Sans';
  justify-content: center;
  padding: ${(props) => props.padding || '0px 0px 10px'};
  width: 100%;
  .text-area {
    background: ${({ theme }) => theme.colors.surfaceInput};
    border: none;
    box-shadow: none;
    font-size: 28px;
    height: 200px;
    outline: none;
    padding: 20px 20px 20px;
    resize: none;
    .rc-textarea {
      height: 100%;
      padding: 0px;
    }
  }
`;

const SearchDiv = styled('div')`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  border: none;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  font-weight: 500;
  height: 48px;
  margin: 10px auto 0 auto;
  padding: 8px 12px;
  width: 90%;
`;

const SearchIconWrapper = styled('div')`
  align-items: center;
  background-image: url(${SearchIcon});
  background-position-x: 50%;
  background-repeat: no-repeat;
  background-size: 16px;
  display: flex;
  height: 16px;
  justify-content: center;
  width: 10%;
`;

const SearchInput = styled('input')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: ${(props) => {
    if (props.value) return props.theme.colors.text;
    return props.theme.colors.textSecondary;
  }};
  display: flex;
  justify-content: start;
  width: 90%;
`;

export const IconContainer = styled('div')`
  align-items: center;
  display: flex;
  height: ${(props) => props.size || '60px'};
  justify-content: center;
  width: ${(props) => props.size || '60px'};
  ${(props) => props.marginRight && `margin-right: ${props.marginRight};`}
`;

/**
 * @param {Function} onRequestClose callback invoked when the close button is clicked
 * @param {String} iconColor color of the close icon
 */
export const CloseButtonContainer = ({ onRequestClose, iconColor }) => {
  const { colors } = useTheme();
  return (
    <CloseButton aria-label='Close modal' onClick={onRequestClose}>
      <IcomoonReact
        iconSet={iconSet}
        size={12}
        icon='remove'
        color={iconColor || colors.text}
        aria-hidden='true'
      />
    </CloseButton>
  );
};

CloseButtonContainer.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
  iconColor: PropTypes.string,
};

CloseButtonContainer.defaultProps = {
  /* Resolved from the theme inside the component when not supplied — a
     defaultProp can't read context. */
  iconColor: undefined,
};

/**
 * @param {String} message message displayed when there is no data to show
 */
export const PickerModalNoData = ({ message }) => {
  const { colors } = useTheme();
  return (
    <NoDataDiv>
      <IconContainer size='60px'>
        <IcomoonReact
          iconSet={iconSet}
          size={60}
          icon='warning'
          color={colors.text}
        />
      </IconContainer>
      <NoDataText>{message}</NoDataText>
    </NoDataDiv>
  );
};

PickerModalNoData.propTypes = {
  message: PropTypes.string.isRequired,
};

/**
 * @param {String} placeholder placeholder text for the search input
 * @param {String} value current value of the search input
 * @param {Function} onChange callback invoked when the search input changes
 * @param {String} ariaLabel accessible label for the search input
 */
export const PickerModalSearch = ({
  placeholder,
  value,
  onChange,
  ariaLabel,
}) => (
  <SearchDiv>
    <SearchIconWrapper />
    <SearchInput
      type='text'
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
    />
  </SearchDiv>
);

PickerModalSearch.propTypes = {
  ariaLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

PickerModalSearch.defaultProps = {
  ariaLabel: undefined,
};

/**
 * @param {Node} children content rendered inside the modal body
 * @param {String} customPadding custom padding for the primary wrapper
 * @param {String} headerText text displayed in the modal header
 * @param {String} headerIcon name of the icomoon icon shown above the header text
 * @param {Number} iconSize size of the header icon in pixels
 * @param {Function} onRequestClose callback invoked when the modal is closed
 */
const PickerModal = ({
  children,
  customPadding,
  headerText,
  headerIcon,
  iconSize,
  onRequestClose,
}) => {
  const { colors } = useTheme();
  return (
    <Wrapper>
      <PrimaryWrapper padding={customPadding}>
        <CloseButtonContainer onRequestClose={onRequestClose} />
        <ModalHeaderDiv hasIcon={!!headerIcon}>
          {headerIcon && (
            <HeaderIconWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={iconSize}
                icon={headerIcon}
                color={colors.text}
              />
            </HeaderIconWrapper>
          )}
          <h4>{headerText}</h4>
        </ModalHeaderDiv>
        {children}
        <BottomDiv />
      </PrimaryWrapper>
    </Wrapper>
  );
};

PickerModal.propTypes = {
  children: PropTypes.node.isRequired,
  customPadding: PropTypes.string,
  headerText: PropTypes.string.isRequired,
  headerIcon: PropTypes.string,
  iconSize: PropTypes.number,
  onRequestClose: PropTypes.func.isRequired,
};

PickerModal.defaultProps = {
  customPadding: '0px 0px 10px 0px',
  headerIcon: '',
  iconSize: 32,
};

export default PickerModal;
